import React, { useCallback, useState } from 'react';
import apiClient, { getErrorMessage } from '../utils/api';
import LegalAccountsCard, {
  type LegalAccountsMetrics,
  type AttorneyOfficeRow,
} from './LegalAccountsCard';
import LegalAttorneyAccountsDialog, { type LegalLitigationAccount } from './LegalAttorneyAccountsDialog';
import BankruptcyPortfolioCard, { type BankruptcyMetrics } from './BankruptcyPortfolioCard';
import BankruptcyAccountsDialog, { type BankruptcyAccount } from './BankruptcyAccountsDialog';
import './PortHealth.css';

interface AttorneyOfficesResponse {
  offices: AttorneyOfficeRow[];
}

interface PaginationState {
  page: number;
  page_size: number;
  total_count: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}

const PAYMENT_METHOD_LABELS: Record<number, string> = {
  0: 'None',
  1: 'Check',
  2: 'Cash',
  4: 'Allotment',
  8: 'ACH (Manual)',
  6: 'CC/Debit (Manual)',
  5: 'CC/Debit (Recurring)',
  7: 'ACH (Web)',
  10: 'Debit Card (Web)',
  9: 'PNM',
  11: 'ACH (CSR)',
  12: 'CC/Debit (CSR)',
  13: 'ACH (Adjustment)',
  14: 'Check (Garnishment)',
  15: 'ACH (IVR)',
  16: 'CC/Debit (IVR)',
  17: 'Check (Lockbox)',
  18: 'Wire',
  19: 'ACH eLockbox',
  20: 'ACH (Text)',
  21: 'CC/Debit (Text)',
};

function paymentMethodLabel(raw: unknown): string {
  if (raw == null) return '';
  const n = typeof raw === 'number' ? raw : Number(String(raw).trim());
  if (Number.isFinite(n) && PAYMENT_METHOD_LABELS[n] != null) return PAYMENT_METHOD_LABELS[n];
  return String(raw);
}

function mapLegalAccount(raw: Record<string, unknown>): LegalLitigationAccount {
  return {
    loan_number: String(raw.loan_number ?? ''),
    customer_name: String(raw.customer_name ?? ''),
    litigation_state: String(raw.litigation_state ?? ''),
    litigation_county: String(raw.litigation_county ?? ''),
    collection_officer: String(raw.collection_officer ?? ''),
    attorney_file_number: String(raw.attorney_file_number ?? ''),
    case_number: String(raw.case_number ?? ''),
    case_filing_date: String(raw.case_filing_date ?? ''),
    service_date: String(raw.service_date ?? ''),
    judgement_date: String(raw.judgement_date ?? ''),
    garn_filing_date: String(raw.garn_filing_date ?? ''),
    garn_service_date: String(raw.garn_service_date ?? ''),
    garn_answer_date: String(raw.garn_answer_date ?? ''),
    last_payment_date: String(raw.last_payment_date ?? ''),
    last_payment_amount: typeof raw.last_payment_amount === 'number' ? raw.last_payment_amount : Number(raw.last_payment_amount) || 0,
  };
}

function mapBankruptcyAccount(raw: Record<string, unknown>): BankruptcyAccount {
  return {
    loan_number: String(raw.loan_number ?? ''),
    customer_name: String(raw.customer_name ?? ''),
    chapter: String(raw.chapter ?? ''),
    filing_date: String(raw.filing_date ?? ''),
    days_past_due: typeof raw.days_past_due === 'number' ? raw.days_past_due : Number(raw.days_past_due) || 0,
    last_payment_date: String(raw.last_payment_date ?? ''),
    last_payment_type: paymentMethodLabel(raw.last_payment_type),
    deferments: typeof raw.deferments === 'number' ? raw.deferments : Number(raw.deferments) || 0,
    secured: typeof raw.secured === 'boolean' ? raw.secured : raw.secured == null ? undefined : Boolean(raw.secured),
  };
}

const LegalBankruptcy: React.FC = () => {
  const [legalMetrics, setLegalMetrics] = useState<LegalAccountsMetrics | null>(null);
  const [attorneyOffices, setAttorneyOffices] = useState<AttorneyOfficeRow[]>([]);
  const [bankruptcyMetrics, setBankruptcyMetrics] = useState<BankruptcyMetrics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [officeDialogOpen, setOfficeDialogOpen] = useState(false);
  const [selectedOfficeLabel, setSelectedOfficeLabel] = useState('');
  const [officeAccounts, setOfficeAccounts] = useState<LegalLitigationAccount[]>([]);
  const [officePagination, setOfficePagination] = useState<PaginationState | null>(null);
  const [officeSorting, setOfficeSorting] = useState({ sort_by: 'loan_number', sort_order: 'ASC' });
  const [officeDialogLoading, setOfficeDialogLoading] = useState(false);
  const [officeDialogError, setOfficeDialogError] = useState<string | null>(null);
  const [officeSearchLoan, setOfficeSearchLoan] = useState('');
  const [officeSearchCustomer, setOfficeSearchCustomer] = useState('');
  const [officeSearchPending, setOfficeSearchPending] = useState(false);

  const [bankruptcyDialogOpen, setBankruptcyDialogOpen] = useState(false);
  const [bankruptcyChapter, setBankruptcyChapter] = useState<'7' | '13'>('7');
  const [bankruptcySecuredFilter, setBankruptcySecuredFilter] = useState<'all' | 'secured' | 'unsecured'>('all');
  const [bankruptcyAccounts, setBankruptcyAccounts] = useState<BankruptcyAccount[]>([]);
  const [bankruptcyPagination, setBankruptcyPagination] = useState<PaginationState | null>(null);
  const [bankruptcySorting, setBankruptcySorting] = useState({ sort_by: 'days_past_due', sort_order: 'DESC' });
  const [bankruptcyDialogLoading, setBankruptcyDialogLoading] = useState(false);
  const [bankruptcyDialogError, setBankruptcyDialogError] = useState<string | null>(null);
  const [bankruptcySearchLoan, setBankruptcySearchLoan] = useState('');
  const [bankruptcySearchCustomer, setBankruptcySearchCustomer] = useState('');
  const [bankruptcySearchPending, setBankruptcySearchPending] = useState(false);

  const loadMetrics = async () => {
    try {
      setLoading(true);
      setError(null);

      const [legalRes, officesRes, bkRes] = await Promise.all([
        apiClient.get('/legal-bankruptcy/legal/portfolio-metrics'),
        apiClient.get('/legal-bankruptcy/legal/attorney-offices'),
        apiClient.get('/legal-bankruptcy/bankruptcy/portfolio-metrics'),
      ]);

      setLegalMetrics(legalRes.data);
      const officesPayload = officesRes.data as AttorneyOfficesResponse;
      setAttorneyOffices(officesPayload.offices ?? []);
      setBankruptcyMetrics(bkRes.data);
    } catch (err: unknown) {
      console.error('Error loading Legal/Bankruptcy metrics:', err);
      setError(getErrorMessage(err));
      setLegalMetrics(null);
      setAttorneyOffices([]);
      setBankruptcyMetrics(null);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    void loadMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadOfficeAccounts = useCallback(
    async (
      officeDisplayName: string,
      page: number,
      sortBy: string,
      sortOrder: string,
      searchLoan: string,
      searchCustomer: string
    ) => {
      setOfficeDialogLoading(true);
      setOfficeSearchPending(false);
      setOfficeDialogError(null);
      try {
        const params = new URLSearchParams({
          attorney_office: officeDisplayName,
          page: String(page),
          page_size: '25',
          sort_by: sortBy,
          sort_order: sortOrder,
        });
        if (searchLoan) params.set('search_loan_number', searchLoan);
        if (searchCustomer) params.set('search_customer_name', searchCustomer);

        const res = await apiClient.get(
          `/legal-bankruptcy/legal/attorney-office-accounts?${params.toString()}`
        );
        const rawList = (res.data.accounts ?? []) as Record<string, unknown>[];
        setOfficeAccounts(rawList.map(mapLegalAccount));
        setOfficePagination(res.data.pagination ?? null);
        if (res.data.sorting) {
          setOfficeSorting({
            sort_by: res.data.sorting.sort_by,
            sort_order: res.data.sorting.sort_order,
          });
        }
      } catch (err: unknown) {
        console.error('Error loading attorney office accounts:', err);
        setOfficeDialogError(getErrorMessage(err));
        setOfficeAccounts([]);
        setOfficePagination(null);
      } finally {
        setOfficeDialogLoading(false);
      }
    },
    []
  );

  const handleAttorneyOfficeClick = (row: AttorneyOfficeRow) => {
    const label = row.attorney_office || '(Unspecified)';
    setSelectedOfficeLabel(label);
    setOfficeSearchLoan('');
    setOfficeSearchCustomer('');
    setOfficeDialogError(null);
    setOfficeDialogOpen(true);
    void loadOfficeAccounts(label, 1, 'loan_number', 'ASC', '', '');
  };

  const handleOfficeDialogClose = () => {
    setOfficeDialogOpen(false);
    setSelectedOfficeLabel('');
    setOfficeAccounts([]);
    setOfficePagination(null);
    setOfficeDialogError(null);
  };

  const fetchAllOfficeAccountsForExport = useCallback(async (): Promise<LegalLitigationAccount[]> => {
    const all: LegalLitigationAccount[] = [];
    let page = 1;
    let totalPages = 1;
    do {
      const params = new URLSearchParams({
        attorney_office: selectedOfficeLabel,
        page: String(page),
        page_size: '100',
        sort_by: officeSorting.sort_by,
        sort_order: officeSorting.sort_order,
      });
      if (officeSearchLoan) params.set('search_loan_number', officeSearchLoan);
      if (officeSearchCustomer) params.set('search_customer_name', officeSearchCustomer);

      const res = await apiClient.get(
        `/legal-bankruptcy/legal/attorney-office-accounts?${params.toString()}`
      );
      const rawList = (res.data.accounts ?? []) as Record<string, unknown>[];
      all.push(...rawList.map(mapLegalAccount));
      const pag = res.data.pagination;
      totalPages = pag?.total_pages ?? 1;
      page += 1;
    } while (page <= totalPages);
    return all;
  }, [selectedOfficeLabel, officeSorting, officeSearchLoan, officeSearchCustomer]);

  const loadBankruptcyAccounts = useCallback(
    async (
      chapter: '7' | '13',
      securedFilter: 'all' | 'secured' | 'unsecured',
      page: number,
      sortBy: string,
      sortOrder: string,
      searchLoan: string,
      searchCustomer: string
    ) => {
      setBankruptcyDialogLoading(true);
      setBankruptcySearchPending(false);
      setBankruptcyDialogError(null);
      try {
        const params = new URLSearchParams({
          chapter,
          page: String(page),
          page_size: '25',
          sort_by: sortBy,
          sort_order: sortOrder,
        });
        if (securedFilter === 'secured') params.set('secured', 'true');
        if (securedFilter === 'unsecured') params.set('secured', 'false');
        if (searchLoan) params.set('search_loan_number', searchLoan);
        if (searchCustomer) params.set('search_customer_name', searchCustomer);

        const res = await apiClient.get(`/legal-bankruptcy/bankruptcy/accounts?${params.toString()}`);
        const rawList = (res.data.accounts ?? []) as Record<string, unknown>[];
        setBankruptcyAccounts(rawList.map(mapBankruptcyAccount));
        setBankruptcyPagination(res.data.pagination ?? null);
        if (res.data.sorting) {
          setBankruptcySorting({
            sort_by: res.data.sorting.sort_by,
            sort_order: res.data.sorting.sort_order,
          });
        }
      } catch (err: unknown) {
        console.error('Error loading bankruptcy accounts:', err);
        setBankruptcyDialogError(getErrorMessage(err));
        setBankruptcyAccounts([]);
        setBankruptcyPagination(null);
      } finally {
        setBankruptcyDialogLoading(false);
      }
    },
    []
  );

  const handleChapterClick = (chapter: '7' | '13') => {
    setBankruptcyChapter(chapter);
    setBankruptcySecuredFilter('all');
    setBankruptcySearchLoan('');
    setBankruptcySearchCustomer('');
    setBankruptcyDialogError(null);
    setBankruptcyDialogOpen(true);
    void loadBankruptcyAccounts(chapter, 'all', 1, 'days_past_due', 'DESC', '', '');
  };

  const handleBankruptcyDialogClose = () => {
    setBankruptcyDialogOpen(false);
    setBankruptcyAccounts([]);
    setBankruptcyPagination(null);
    setBankruptcyDialogError(null);
  };

  const fetchAllBankruptcyAccountsForExport = useCallback(async (): Promise<BankruptcyAccount[]> => {
    const all: BankruptcyAccount[] = [];
    let page = 1;
    let totalPages = 1;
    do {
      const params = new URLSearchParams({
        chapter: bankruptcyChapter,
        page: String(page),
        page_size: '100',
        sort_by: bankruptcySorting.sort_by,
        sort_order: bankruptcySorting.sort_order,
      });
      if (bankruptcySecuredFilter === 'secured') params.set('secured', 'true');
      if (bankruptcySecuredFilter === 'unsecured') params.set('secured', 'false');
      if (bankruptcySearchLoan) params.set('search_loan_number', bankruptcySearchLoan);
      if (bankruptcySearchCustomer) params.set('search_customer_name', bankruptcySearchCustomer);

      const res = await apiClient.get(`/legal-bankruptcy/bankruptcy/accounts?${params.toString()}`);
      const rawList = (res.data.accounts ?? []) as Record<string, unknown>[];
      all.push(...rawList.map(mapBankruptcyAccount));
      const pag = res.data.pagination;
      totalPages = pag?.total_pages ?? 1;
      page += 1;
    } while (page <= totalPages);
    return all;
  }, [
    bankruptcyChapter,
    bankruptcySorting,
    bankruptcySecuredFilter,
    bankruptcySearchLoan,
    bankruptcySearchCustomer,
  ]);

  return (
    <div className="port-health-container">
      <div className="port-health-header">
        <h1>
          ⚖️ Legal &amp; Bankruptcy Monitoring{' '}
          <span className="version-watermark">v1.0.0</span>
        </h1>
        <p className="subtitle">
          Legal Accounts: portfolio-wide JDG / GDG / WIP mix and counts by attorney office.
          Bankruptcy summary shows Chapter 7 / 13 mix.
        </p>

        <div className="header-actions">
          <button
            onClick={() => void loadMetrics()}
            className="refresh-button"
            disabled={loading}
          >
            {loading ? 'Loading...' : '🔄 Refresh Data'}
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <p>{error}</p>
        </div>
      )}

      <div className="managers-grid" style={{ marginTop: 0 }}>
        <LegalAccountsCard
          metrics={legalMetrics}
          offices={attorneyOffices}
          loading={loading}
          onAttorneyOfficeClick={handleAttorneyOfficeClick}
        />

        <BankruptcyPortfolioCard
          metrics={bankruptcyMetrics}
          loading={loading}
          onChapterClick={handleChapterClick}
        />
      </div>

      <LegalAttorneyAccountsDialog
        isOpen={officeDialogOpen}
        onClose={handleOfficeDialogClose}
        accounts={officeAccounts}
        title={`Legal accounts — ${selectedOfficeLabel}`}
        loading={officeDialogLoading}
        errorMessage={officeDialogError}
        pagination={officePagination ?? undefined}
        sorting={officeSorting}
        onPageChange={(page) =>
          void loadOfficeAccounts(
            selectedOfficeLabel,
            page,
            officeSorting.sort_by,
            officeSorting.sort_order,
            officeSearchLoan,
            officeSearchCustomer
          )
        }
        onSortChange={(sortBy, sortOrder) =>
          void loadOfficeAccounts(
            selectedOfficeLabel,
            1,
            sortBy,
            sortOrder,
            officeSearchLoan,
            officeSearchCustomer
          )
        }
        onSearchChange={(loan, customer) => {
          setOfficeSearchLoan(loan);
          setOfficeSearchCustomer(customer);
          setOfficeSearchPending(true);
          void loadOfficeAccounts(selectedOfficeLabel, 1, officeSorting.sort_by, officeSorting.sort_order, loan, customer);
        }}
        searchLoanNumber={officeSearchLoan}
        searchCustomerName={officeSearchCustomer}
        isSearchPending={officeSearchPending}
        onSearchComplete={() => setOfficeSearchPending(false)}
        onExportAllData={fetchAllOfficeAccountsForExport}
      />

      <BankruptcyAccountsDialog
        isOpen={bankruptcyDialogOpen}
        onClose={handleBankruptcyDialogClose}
        accounts={bankruptcyAccounts}
        title={`Bankruptcy — Chapter ${bankruptcyChapter}`}
        chapter={bankruptcyChapter}
        securedFilter={bankruptcySecuredFilter}
        onSecuredFilterChange={(value) => {
          setBankruptcySecuredFilter(value);
          void loadBankruptcyAccounts(
            bankruptcyChapter,
            value,
            1,
            bankruptcySorting.sort_by,
            bankruptcySorting.sort_order,
            bankruptcySearchLoan,
            bankruptcySearchCustomer
          );
        }}
        loading={bankruptcyDialogLoading}
        errorMessage={bankruptcyDialogError}
        pagination={bankruptcyPagination ?? undefined}
        sorting={bankruptcySorting}
        onPageChange={(page) =>
          void loadBankruptcyAccounts(
            bankruptcyChapter,
            bankruptcySecuredFilter,
            page,
            bankruptcySorting.sort_by,
            bankruptcySorting.sort_order,
            bankruptcySearchLoan,
            bankruptcySearchCustomer
          )
        }
        onSortChange={(sortBy, sortOrder) =>
          void loadBankruptcyAccounts(
            bankruptcyChapter,
            bankruptcySecuredFilter,
            1,
            sortBy,
            sortOrder,
            bankruptcySearchLoan,
            bankruptcySearchCustomer
          )
        }
        onSearchChange={(loan, customer) => {
          setBankruptcySearchLoan(loan);
          setBankruptcySearchCustomer(customer);
          setBankruptcySearchPending(true);
          void loadBankruptcyAccounts(bankruptcyChapter, bankruptcySecuredFilter, 1, bankruptcySorting.sort_by, bankruptcySorting.sort_order, loan, customer);
        }}
        searchLoanNumber={bankruptcySearchLoan}
        searchCustomerName={bankruptcySearchCustomer}
        isSearchPending={bankruptcySearchPending}
        onSearchComplete={() => setBankruptcySearchPending(false)}
        onExportAllData={fetchAllBankruptcyAccountsForExport}
      />
    </div>
  );
};

export default LegalBankruptcy;
